import * as ToastPrimitivo from "@radix-ui/react-toast";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { cn } from "./utils";

export interface OpcionesToast {
  titulo: string;
  descripcion?: string;
  accionTexto?: string;
  onAccion?: () => void;
}

interface ToastInterno extends OpcionesToast {
  id: number;
}

interface ContextoToastValor {
  mostrarToast: (opciones: OpcionesToast) => void;
}

const ContextoToast = createContext<ContextoToastValor | null>(null);

/** Proveedor global de toasts con soporte de acción ("Deshacer", etc.). */
export function ProveedorToast({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastInterno[]>([]);
  const siguienteId = useMemo(() => ({ actual: 0 }), []);

  const mostrarToast = useCallback(
    (opciones: OpcionesToast) => {
      siguienteId.actual += 1;
      const id = siguienteId.actual;
      setToasts((actual) => [...actual, { ...opciones, id }]);
    },
    [siguienteId],
  );

  const quitarToast = useCallback((id: number) => {
    setToasts((actual) => actual.filter((t) => t.id !== id));
  }, []);

  return (
    <ContextoToast.Provider value={{ mostrarToast }}>
      <ToastPrimitivo.Provider swipeDirection="right">
        {children}
        {toasts.map((toast) => (
          <ToastPrimitivo.Root
            key={toast.id}
            duration={5000}
            onOpenChange={(abierto) => {
              if (!abierto) quitarToast(toast.id);
            }}
            className={cn(
              "flex items-center justify-between gap-4 rounded-control bg-texto px-4 py-3 text-superficie shadow-elevada",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
            )}
          >
            <div>
              <ToastPrimitivo.Title className="text-cuerpo font-medium">{toast.titulo}</ToastPrimitivo.Title>
              {toast.descripcion ? (
                <ToastPrimitivo.Description className="text-nota text-superficie/80">
                  {toast.descripcion}
                </ToastPrimitivo.Description>
              ) : null}
            </div>
            {toast.accionTexto && toast.onAccion ? (
              <ToastPrimitivo.Action asChild altText={toast.accionTexto}>
                <button
                  type="button"
                  onClick={toast.onAccion}
                  className="min-h-control shrink-0 rounded-control px-3 text-cuerpo font-bold text-primario underline"
                >
                  {toast.accionTexto}
                </button>
              </ToastPrimitivo.Action>
            ) : null}
          </ToastPrimitivo.Root>
        ))}
        <ToastPrimitivo.Viewport className="fixed bottom-4 right-4 z-50 flex w-[min(90vw,24rem)] flex-col gap-2" />
      </ToastPrimitivo.Provider>
    </ContextoToast.Provider>
  );
}

export function useToast(): ContextoToastValor {
  const contexto = useContext(ContextoToast);
  if (!contexto) {
    throw new Error("useToast debe usarse dentro de <ProveedorToast>");
  }
  return contexto;
}
